/**
 * Odczyt zawartosci tokena JWT po stronie przegladarki.
 *
 * UWAGA: to jest wylacznie dekodowanie, BEZ weryfikacji podpisu. Podpis RS256
 * sprawdza api-gateway przez JWKS. Danych z tokena uzywamy tylko do sterowania
 * interfejsem (co pokazac, dokad przekierowac) — nigdy jako zrodla uprawnien.
 * Kazda decyzja bezpieczenstwa zapada po stronie backendu.
 */

/** Zestaw claimow wystawianych przez `user-service` (patrz JwtService#generateAccessToken). */
export interface JwtPayload {
  /** Identyfikator uzytkownika (UUID). */
  sub: string;
  email?: string;
  globalRoles?: string[];
  /** Czas wygasniecia w sekundach epoki uniksowej. */
  exp?: number;
  /** Czas wystawienia w sekundach epoki uniksowej. */
  iat?: number;
}

/**
 * Dekoduje ciag base64url na tekst, obslugujac znaki spoza ASCII (np. polskie litery).
 */
function decodeBase64Url(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.codePointAt(0) ?? 0);
  return new TextDecoder().decode(bytes);
}

/**
 * Zwraca zdekodowana zawartosc tokena albo `null`, gdy token jest pusty lub uszkodzony.
 */
export function decodeJwt(token: string): JwtPayload | null {
  const segments = token.split('.');
  if (segments.length !== 3) return null;

  const payloadSegment = segments[1];
  if (!payloadSegment) return null;

  try {
    const parsed: unknown = JSON.parse(decodeBase64Url(payloadSegment));
    if (typeof parsed !== 'object' || parsed === null) return null;

    const claims = parsed as Record<string, unknown>;
    if (typeof claims.sub !== 'string') return null;

    return {
      sub: claims.sub,
      ...(typeof claims.email === 'string' ? { email: claims.email } : {}),
      ...(Array.isArray(claims.globalRoles)
        ? { globalRoles: claims.globalRoles.filter((role): role is string => typeof role === 'string') }
        : {}),
      ...(typeof claims.exp === 'number' ? { exp: claims.exp } : {}),
      ...(typeof claims.iat === 'number' ? { iat: claims.iat } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * Sprawdza, czy token wygasl. `leewaySeconds` pozwala odswiezyc go z wyprzedzeniem,
 * zanim brama zdazy go odrzucic.
 */
export function isTokenExpired(token: string, leewaySeconds = 30): boolean {
  const payload = decodeJwt(token);
  if (!payload?.exp) return true;
  return payload.exp * 1000 <= Date.now() + leewaySeconds * 1000;
}
